import { useState, type Dispatch, type SetStateAction } from "react";
import type { Order } from "../../lib/types.ts";
import { BOARD_GROUPS } from "../../config/board.ts";
import { bestellungSammelAendern, boardStatusSammelAendern } from "../../api/bestellungen.ts";
import { meldeFehler, meldeNachladeFehler } from "../../state/fehlerStapel.ts";

/**
 * Ergebnis eines Nachladens nach einem Schreiben (`App.tsx#sammelEintragNachladen`): gelungen, oder gescheitert mit der
 * Ursache. Ein `null` vom Server (die Bestellung gibt es nicht mehr) gilt als gelungen - es gibt nichts anzuzeigen.
 * Die Ursache bleibt erhalten, damit der Aufrufer sie hinter seinem Satz zeigen kann (`meldeNachladeFehler`) statt sie zu
 * verschlucken.
 */
export type NachladeErgebnis = { ok: true } | { ok: false; ursache: unknown };

type NachladeFehlschlag = Extract<NachladeErgebnis, { ok: false }>;

/**
 * Die gescheiterten Ergebnisse, in der Reihenfolge der angefragten Bestellungen. Eine Sammelmeldung ueber N Bestellungen
 * nennt die Ursache der ERSTEN (Spec "Nachladen nach einem Schreiben", Punkt 2) - `[0]`.
 */
export function nachladeFehlschlaege(ergebnisse: readonly NachladeErgebnis[]): NachladeFehlschlag[] {
  return ergebnisse.filter((ergebnis): ergebnis is NachladeFehlschlag => !ergebnis.ok);
}

/**
 * R4 ueber mehrere Nachladeergebnisse (Fehleranzeige Paket 2, Task 10): scheiterte keines, passiert nichts; sonst nennt
 * `satz` die Anzahl der gescheiterten, und die Ursache der ERSTEN steht dahinter, ausser sie raet zum Wiederholen
 * (`meldeNachladeFehler`). Der Aufrufer entscheidet vorher, ob ueberhaupt gemeldet wird (Unmount).
 */
export function meldeNachladeFehlschlaege(
  ergebnisse: readonly NachladeErgebnis[],
  satz: (anzahl: number) => string,
): void {
  const fehlschlaege = nachladeFehlschlaege(ergebnisse);
  const erster = fehlschlaege[0];
  if (erster !== undefined) meldeNachladeFehler(erster.ursache, satz(fehlschlaege.length));
}

/**
 * Sammel-Status und Sammel-Bereich: schreiben, betroffene Eintraege nachladen und Auswahl bereinigen.
 *
 * MELDUNGEN (Fehleranzeige Paket 1): ein VOLLER Erfolg ist eine Bestaetigung und bleibt im Toast
 * (`zeigeMeldung`). Alles andere sagt, dass die Absicht nicht oder nur teilweise ausgefuehrt wurde (Konflikte,
 * uebersprungene Sammelsendungs-Mitglieder, nicht aktuelle Ansicht, Fehlschlag) und geht in den Fehlerstapel -
 * der Toast haelt es nur 2,2 s und ueberschriebe es beim naechsten Aufruf. Die Entscheidung haengt am
 * Laufzeitergebnis: `teile` hat genau dann nur die Basis, wenn nichts angehaengt wurde.
 *
 * NACHLADEN (Fehleranzeige Paket 2, Task 10): ist ALLES geschrieben und scheitert nur das Nachladen (R4), geht die Meldung
 * ueber `meldeNachladeFehler` - der Satz bleibt, die Ursache der ersten gescheiterten Bestellung steht dahinter, ausser sie
 * raet zum Wiederholen (hinter "geaendert" liesse ein "Bitte gleich noch einmal versuchen" den Nutzer ein zweites Mal
 * schreiben). Ein Teilerfolg (Konflikte oder uebersprungene Sammelsendungs-Mitglieder gehoeren dazu) bleibt ein Fehlereintrag
 * ueber `meldeFehler` ohne Ursache: die Meldung erzaehlt dann mehrere Dinge, und keine einzelne Ursache gehoert zu allen.
 */
export function useBoardSammelaktionen(
  bestellungen: readonly Order[],
  ausgewaehlteBestellungen: readonly string[],
  sammelEintragNachladen: (id: string) => Promise<NachladeErgebnis>,
  setSammelStatusDialogOffen: Dispatch<SetStateAction<boolean>>,
  setAuswahlLeerenSignal: Dispatch<SetStateAction<number>>,
  zeigeMeldung: (text: string) => void,
) {
  /**
   * NACHBESSERUNG 2 (Pruefung 97e5bffb, Gering): waehrend `boardStatusSammelAendern` samt der
   * anschliessenden Nachlade-`Promise.all` unterwegs ist - gereicht an `SammelStatusDialog`s neue
   * Prop `laeuft`, die dort den Uebernehmen-Knopf sperrt. Ohne diese Sperre loeste ein zweiter Klick
   * (gemessen: 40 markierte Zeilen, viele Nachlade-GETs, Dialog blieb sekundenlang offen) einen
   * zweiten Bulk-Request mit inzwischen veralteten `rowVersion`-Werten aus. `false` sowohl nach einem
   * Erfolg (dort ohnehin gleichzeitig mit `setSammelStatusDialogOffen(false)`) als auch im `.catch`
   * unten (der Dialog bleibt dort bewusst OFFEN - siehe dortiger Kommentar -, der Knopf muss fuer
   * einen erneuten Versuch also wieder bedienbar werden).
   */
  const [sammelStatusLaeuft, setSammelStatusLaeuft] = useState(false);

  /**
   * Schreibt den gewaehlten Status fuer JEDE Bestellung in `ausgewaehlteBestellungen` - analog
   * `App.tsx#drawerStatusAendern`/`App.tsx#boardGruppeAendern`, in EINEM Sammelaufruf statt vieler Einzelner
   * (`boardStatusSammelAendern`, `PATCH .../board-status/bulk`).
   *
   * `extrasRowVersion`-Ermittlung samt Rueckfall `0`: WOERTLICH dieselbe Formel wie an den beiden
   * Vorbildstellen (siehe dortige Begruendung, hier nicht wiederholt). Ids ohne (mehr) geladene
   * Bestellung werden STILL uebersprungen, kein Absturz - sie koennten zwischen dem Markieren und
   * diesem Klick aus dem Speicher gefallen sein (SSE-Nachladen, Archivierung durch eine andere
   * Sitzung, …). Bleibt danach KEINE Id mehr uebrig, bricht die Funktion NICHT mehr stillschweigend
   * ab (Nachbesserung, Gering) - eine ehrliche Meldung, UND der Dialog schliesst, statt offen zu
   * haengen.
   *
   * ================= NACHBESSERUNG (Pruefung e9f0edb, Kritisch+Wichtig1, 2026-09-02) ================
   *
   * HIER STAND BIS DAHIN ein direkter Merge der Serverantwort ueber `updated[]`/`entschachtele` in
   * den Store (KEIN `nachladen()` je betroffener Id). Befund: 2132 von 2138 Bestellungen haben KEINE
   * eigene `order_extras`-Zeile - ihre Speicher-Id ist die ABGELEITETE Kennung `jtl-<n>`
   * (`api/jtlBestellungen.ts#abgeleiteteKennung`), keine echte uuid. Schrieb der Bulk-Endpunkt
   * ERSTMALS in eine solche Bestellung, legte er serverseitig eine `orders`-Zeile an und lieferte in
   * `updated[i].orderId` die NEUE uuid - NICHT die gesendete `jtl-<n>`-Kennung. Der Merge dispatchte
   * diese Antwort per `entschachtele(eintrag)` UNTER `eintrag.orderId` - der Reduzierer
   * (`reduzierer.ts#listeErsetzen`) fand unter dieser NEUEN uuid keine bestehende Zeile (die Zeile im
   * Store stand ja weiter unter `jtl-<n>`) und HAENGTE einen neuen, fast leeren Datensatz AN, statt
   * die eigentliche Zeile zu ersetzen: die markierte Zeile behielt ihren alten Status, daneben
   * erschien eine leere Geisterzeile, der Toast meldete trotzdem Erfolg. Betraf den NORMALFALL, nicht
   * einen Randfall.
   *
   * DER FIX: fuer JEDE erfolgreich geschriebene Id (`items[].id` abzueglich `conflicts[].id`, NICHT
   * `updated[].orderId`) `sammelEintragNachladen(<ORIGINAL GESENDETE id>)` (siehe oben) - GENAU wie
   * der bereits bestehende Einzelweg (`boardStatusAendern`, `api/bestellungen.ts`, endet mit `return
   * nachladen(id)`). `bestellungLaden(id)` fragt frisch unter GENAU dieser (ALTEN) Kennung ab und
   * loest `jtl-<n>` erneut korrekt auf - auch fuer eine Bestellung, deren Hub-Zeile gerade erst durch
   * DIESEN Schreibvorgang entstanden ist. `listeErsetzen` sucht die zu ersetzende Zeile UNTER DER
   * KENNUNG, MIT DER `versende(...)` AUFGERUFEN WIRD (siehe dortiger Kopfkommentar "`id` ist die
   * Kennung, UNTER DER GESUCHT wird") - nicht unter der `id`, die der nachgeladene Datensatz selbst
   * traegt. Der `jtl-<n>`-Eintrag im Store wird deshalb GEFUNDEN und durch den vollstaendigen,
   * frischen Datensatz ERSETZT (der jetzt seinerseits die neue uuid als `id` traegt) - keine
   * Geisterzeile, korrekt aktualisiert unter derselben Zeile.
   *
   * VORAUSSETZUNG (Vertrag, gilt laut Auftrag bereits): `conflicts[].id` traegt die URSPRUENGLICH
   * GESENDETE Kennung, nicht die serverseitig aufgeloeste uuid - sonst liesse sich die Menge der
   * ERFOLGREICHEN Ids nicht korrekt aus `items` filtern.
   *
   * `Promise.all`, NICHT seriell: die N Nachlade-GETs sind voneinander unabhaengig.
   *
   * Die Meldung baut seither auf `items.length`/`conflictIds.size`, NICHT mehr auf `updated.length` -
   * `updated` wird fuer die Store-Aktualisierung nicht mehr gebraucht (siehe `boardStatusSammelAendern`s
   * Kopfkommentar: die Rohantwort bleibt dieselbe, nur dieser Aufrufer wertet sie jetzt anders aus).
   * Bei 0 erfolgreichen Schreibungen (Nachbesserung, Gering) eine EIGENE, kuerzere Formulierung ohne
   * "weitere" - "Status für 0 Bestellungen geändert. N weitere waren nicht mehr aktuell…" waere schief.
   *
   * ================= NACHBESSERUNG 2 (Pruefung 97e5bffb, Wichtig 1+2, 2026-09-02) =================
   *
   * WICHTIG 1 - Auswahl verwaist nach erfolgreichem Sammel-Statuswechsel: der Geisterzeilen-Fix oben
   * laedt jede erfolgreich geschriebene Bestellung unter ihrer URSPRUENGLICH GESENDETEN Id nach - fuer
   * die 2132 von 2138 Bestellungen OHNE eigene `order_extras`-Zeile ERSETZT `listeErsetzen` den
   * Eintrag dabei zwar unter der ALTEN Kennung, der nachgeladene DATENSATZ SELBST traegt aber die neu
   * aufgeloeste uuid als `id` (siehe oben). `BoardSeite`s Auswahl (`ausgewaehlt`, lokaler State dort)
   * haengt weiterhin an der ALTEN Kennung - die betroffene Zeile verliert dadurch ihre sichtbare
   * Markierung, OHNE dass sich die Auswahl selbst (Groesse, Werkzeugleiste-Zaehler) aendert. Ein
   * zweiter Sammelwechsel auf der augenscheinlich unveraenderten Auswahl sendet die verwaiste alte
   * Kennung dann nicht mehr sinnvoll mit ("Keine der markierten Bestellungen konnte geändert werden.")
   * - der REGELFALL, kein Randfall.
   *
   * FIX: `setAuswahlLeerenSignal((n) => n + 1)` NACH jedem Sammel-Statuswechsel mit MINDESTENS einer
   * erfolgreichen Aenderung (`erfolgreicheIds.length > 0`) - `BoardSeite`s neue Prop
   * `auswahlLeerenSignal` (siehe dortiger Kopfkommentar zur Bauart) leert die Auswahl daraufhin
   * vollstaendig. AUCH bei einem TEILWEISEN Erfolg (uebrige `conflicts`): die einfachere, im Auftrag
   * empfohlene Variante leert die GESAMTE Auswahl statt selektiv nur die erfolgreichen Ids - ein
   * erneuter Versuch markiert bei Bedarf neu. Die vorherige Entscheidung "Auswahl bewusst unangetastet
   * lassen" (siehe die vorherige Fassung dieses Kommentars in der Historie) ist damit ueberholt: sie
   * galt nur, solange die Auswahl nach einem Erfolg tatsaechlich vollstaendig und sinnvoll bestehen
   * blieb - genau das stimmt seit dem Geisterzeilen-Fix fuer den Regelfall nicht mehr.
   *
   * WICHTIG 2 - fehlgeschlagenes Nachladen blieb unsichtbar: `sammelEintragNachladen` liefert seither ein
   * `NachladeErgebnis` (siehe `App.tsx#sammelEintragNachladen`; zuerst ein `boolean`, seit Fehleranzeige Paket 2, Task 10
   * mit der Ursache) statt den Fehlschlag nur zu protokollieren. Die
   * Meldung unten unterscheidet einen Nachlade-Fehlschlag jetzt EHRLICH von einem Schreibkonflikt -
   * zwei verschiedene Gruende, warum eine Zeile am Ende nicht aktuell aussieht: beim Konflikt hat der
   * Server GAR NICHT geschrieben (die Zeile zeigt zurecht den alten Stand), beim Nachladefehler HAT er
   * gespeichert, nur die ANZEIGE weiss noch nichts davon (die Zeile zeigt zu Unrecht den alten Stand).
   * Wortlaut angelehnt an `App.tsx#NACHLADEN_FEHLGESCHLAGEN_MELDUNG` ("die Ansicht ist [...] nicht mehr
   * aktuell. Bitte neu laden."), hier als drittes, unabhaengiges Anhaengsel an dieselbe Meldung statt
   * eines eigenen, zweiten Toasts (der den ersten sofort ueberschriebe, siehe Kopfkommentar
   * `sammelEintragNachladen`).
   */
  function sammelStatusAendern(status: string): void {
    const items: Array<{ id: string; rowVersion: number }> = [];
    for (const id of ausgewaehlteBestellungen) {
      const bestellung = bestellungen.find((b) => b.id === id);
      if (!bestellung) continue;
      const extrasRowVersion = Number((bestellung as { extrasRowVersion?: unknown }).extrasRowVersion) || 0;
      items.push({ id, rowVersion: extrasRowVersion });
    }
    if (!items.length) {
      setSammelStatusDialogOffen(false);
      meldeFehler("Keine der markierten Bestellungen konnte geändert werden.");
      return;
    }

    // Gering (Nachbesserung 2): waehrend des Rundlaufs gesperrt - siehe `SammelStatusDialog.tsx`s
    // `laeuft`-Prop und die Begruendung am State-Hook oben.
    setSammelStatusLaeuft(true);
    boardStatusSammelAendern(items, status)
      .then(({ conflicts }) => {
        const conflictIds = new Set(conflicts.map((c) => c.id));
        // Mitglieder einer Sammelsendung (Grund `shipment_parent`): ihr Status wird zentral gepflegt,
        // das Backend hat sie uebersprungen. Getrennt von echten Versionskonflikten gemeldet.
        const sammelIds = new Set(conflicts.filter((c) => c.reason === "shipment_parent").map((c) => c.id));
        const versionIds = new Set([...conflictIds].filter((id) => !sammelIds.has(id)));
        const erfolgreicheIds = items.map((i) => i.id).filter((id) => !conflictIds.has(id));
        return Promise.all(erfolgreicheIds.map((id) => sammelEintragNachladen(id))).then((ergebnisse) => {
          setSammelStatusDialogOffen(false);
          setSammelStatusLaeuft(false);
          if (erfolgreicheIds.length === 0) {
            // Nachbesserung (Gering): eigene, kuerzere Formulierung ohne "weitere" - der Server hat
            // tatsaechlich geantwortet, nur war KEINE der markierten Bestellungen mehr aktuell. Kein
            // Nachladen lief (leeres `erfolgreicheIds`), also auch kein Leeren-Signal - die Auswahl
            // bleibt fuer einen sofortigen erneuten Versuch bestehen.
            if (sammelIds.size === 0) {
              meldeFehler(
                `Keine der ${conflictIds.size} markierten Bestellungen war noch aktuell - der Status wurde nicht geändert.`,
              );
            } else if (versionIds.size === 0) {
              meldeFehler(
                `Keine der ${sammelIds.size} markierten Bestellungen wurde geändert: Ihr Status wird zentral in der Sammelsendung gepflegt.`,
              );
            } else {
              // kein-r4: Totalablehnung durch Versionskonflikt, nichts geschrieben und nichts nachgeladen - "nicht mehr aktuell" meint die vom Server abgelehnte Version, nicht die Anzeige
              meldeFehler(
                `Keine der ${conflictIds.size} markierten Bestellungen wurde geändert: ${versionIds.size} war nicht mehr aktuell, bei ${sammelIds.size} wird der Status zentral in der Sammelsendung gepflegt.`,
              );
            }
            return;
          }
          // NACHBESSERUNG 2, Wichtig 1: mindestens EINE erfolgreiche Aenderung - siehe Kopfkommentar.
          setAuswahlLeerenSignal((n) => n + 1);

          const basis = `Status für ${erfolgreicheIds.length} ${
            erfolgreicheIds.length === 1 ? "Bestellung" : "Bestellungen"
          } geändert.`;
          // NACHBESSERUNG 2, Wichtig 2: Konfliktanhaengsel und Nachladefehler-Anhaengsel sind
          // UNABHAENGIG voneinander moeglich (verschiedene Ids koennen von je einem betroffen sein) -
          // beide zusammen ergeben eine dreiteilige Meldung.
          const teile = [basis];
          if (versionIds.size) {
            teile.push(
              `${versionIds.size} ${
                versionIds.size === 1
                  ? "weitere Bestellung war nicht mehr aktuell und blieb unverändert."
                  : "weitere Bestellungen waren nicht mehr aktuell und blieben unverändert."
              }`,
            );
          }
          if (sammelIds.size) {
            teile.push(
              `${sammelIds.size} ${
                sammelIds.size === 1 ? "Bestellung" : "Bestellungen"
              } übersprungen: Ihr Status wird zentral in der Sammelsendung gepflegt.`,
            );
          }
          const nachladeFehler = nachladeFehlschlaege(ergebnisse);
          if (nachladeFehler.length) {
            teile.push(
              `Bei ${nachladeFehler.length} davon ist die Ansicht nicht mehr aktuell - bitte neu laden.`,
            );
          }
          // Nur die Basis = alles gelungen = Bestaetigung (Toast); jedes Anhaengsel macht es zum Teilerfolg.
          if (teile.length === 1) zeigeMeldung(basis);
          // Task 10, R4: alles geschrieben, nur das Nachladen scheiterte - die Ursache der ersten gehoert hinter den Satz.
          else if (versionIds.size === 0 && sammelIds.size === 0) meldeNachladeFehler(nachladeFehler[0]!.ursache, teile.join(" "));
          else meldeFehler(teile.join(" "));
        });
      })
      .catch((fehler: unknown) => {
        console.error("Sammel-Statuswechsel konnte nicht geschrieben werden:", fehler);
        meldeFehler(fehler, "Der Status konnte nicht geändert werden.");
        setSammelStatusLaeuft(false);
      });
  }

  /**
   * Sammel-Bereichswechsel per Drag-and-Drop (Auftrag "Sammel-Bereichswechsel per
   * Drag-and-Drop", der LETZTE fehlende Teil aus "Auf der Bestellübersicht Sammeländerungen für
   * Status und Bereich"): zieht der Nutzer eine MARKIERTE Zeile bei Mehrfachauswahl (Groesse > 1)
   * in eine andere Bereichs-Spalte, wandert die GESAMTE aktuelle Auswahl mit (`BoardSeite`s neue
   * Prop `onSammelAendern`, siehe dortiger Kopfkommentar) - GENAU dasselbe Muster wie
   * `sammelStatusAendern` oben (Geisterzeilen-Fix samt `sammelEintragNachladen`,
   * `auswahlLeerenSignal`), nur mit `patch` (generisch, `bestellungSammelAendern`) statt
   * `status` (`boardStatusSammelAendern`). Die dortigen Kopfkommentare (Geisterzeilen-Befund,
   * `auswahlLeerenSignal`-Bauart) gelten hier UNVERAENDERT und werden hier nicht wiederholt.
   *
   * `patch` geht UNUEBERSETZT (camelCase, `{ boardGroupOverride }`) an `bestellungSammelAendern` -
   * GENAU wie am Einzelweg (`App.tsx#boardGruppeAendern` reicht sein `patch` ebenso unuebersetzt an
   * `bestellungAendern`): die Umformung auf snake_case (`gruppeUndFelder`) sitzt seit der
   * Nachbesserung (Pruefung 78d5a074, Kritisch) INNERHALB von `bestellungSammelAendern`
   * (`api/bestellungen.ts`), nicht hier - siehe dortiger Kopfkommentar. Vorher fehlte dieser
   * Schritt komplett, `felder` ging camelCase auf die Leitung, und JEDER Sammel-Bereichswechsel
   * scheiterte serverseitig an `FeldNichtInGruppeError` (400).
   *
   * `extrasRowVersion`-Ermittlung samt Rueckfall `0`: WOERTLICH dieselbe Formel wie an
   * `drawerStatusAendern`/`boardGruppeAendern`/`sammelStatusAendern`. Ids ohne (mehr) geladene
   * Bestellung werden STILL uebersprungen - sie koennten zwischen dem Markieren und dem Ablegen
   * aus dem Speicher gefallen sein.
   *
   * Bleibt `items` danach leer, gibt es - anders als beim Statusweg - KEINEN Dialog, der zu
   * schliessen waere: nur eine ehrliche Meldung, kein Request.
   *
   * Zielbereich-Label in der Meldung: `patch` selbst traegt nur die `boardGroupOverride`-Id, nicht
   * das Label - `BOARD_GROUPS` (`config/board.ts`) hat nur drei Eintraege und ist bereits ein
   * reines Konstanten-Array (kein Netzaufruf, keine neue Abhaengigkeit), das Nachschlagen kostet
   * hier also weniger als eine Meldung ohne Zielnamen an Aussagekraft verlieren wuerde.
   */
  function boardGruppeSammelAendern(ids: readonly string[], patch: { boardGroupOverride: string }): void {
    const items: Array<{ id: string; rowVersion: number }> = [];
    for (const id of ids) {
      const bestellung = bestellungen.find((b) => b.id === id);
      if (!bestellung) continue;
      const extrasRowVersion = Number((bestellung as { extrasRowVersion?: unknown }).extrasRowVersion) || 0;
      items.push({ id, rowVersion: extrasRowVersion });
    }
    if (!items.length) {
      meldeFehler("Keine der markierten Bestellungen konnte verschoben werden.");
      return;
    }

    const zielLabel = BOARD_GROUPS.find((g) => g.id === patch.boardGroupOverride)?.label;

    bestellungSammelAendern(items, patch)
      .then(({ conflicts }) => {
        const conflictIds = new Set(conflicts.map((c) => c.id));
        const erfolgreicheIds = items.map((i) => i.id).filter((id) => !conflictIds.has(id));
        return Promise.all(erfolgreicheIds.map((id) => sammelEintragNachladen(id))).then((ergebnisse) => {
          if (erfolgreicheIds.length === 0) {
            // Analog `sammelStatusAendern`s Gering-Fix: eigene, kuerzere Formulierung ohne
            // "weitere". Kein Nachladen lief (leeres `erfolgreicheIds`), also auch kein
            // Leeren-Signal - die Auswahl bleibt fuer einen sofortigen erneuten Versuch bestehen.
            meldeFehler(
              `Keine der ${conflictIds.size} markierten Bestellungen war noch aktuell - sie wurden nicht verschoben.`,
            );
            return;
          }
          // Mindestens EINE erfolgreiche Aenderung - siehe Kopfkommentar `auswahlLeerenSignal`.
          setAuswahlLeerenSignal((n) => n + 1);

          const anzahlWort = erfolgreicheIds.length === 1 ? "Bestellung" : "Bestellungen";
          const basis = zielLabel
            ? `${erfolgreicheIds.length} ${anzahlWort} nach „${zielLabel}“ verschoben.`
            : `${erfolgreicheIds.length} ${anzahlWort} verschoben.`;
          // Konfliktanhaengsel und Nachladefehler-Anhaengsel sind UNABHAENGIG voneinander moeglich
          // (verschiedene Ids koennen von je einem betroffen sein) - beide zusammen ergeben eine
          // dreiteilige Meldung, wie bei `sammelStatusAendern`.
          const teile = [basis];
          if (conflictIds.size) {
            teile.push(
              `${conflictIds.size} ${
                conflictIds.size === 1
                  ? "weitere Bestellung war nicht mehr aktuell und blieb unverändert."
                  : "weitere Bestellungen waren nicht mehr aktuell und blieben unverändert."
              }`,
            );
          }
          const nachladeFehler = nachladeFehlschlaege(ergebnisse);
          if (nachladeFehler.length) {
            teile.push(
              `Bei ${nachladeFehler.length} davon ist die Ansicht nicht mehr aktuell - bitte neu laden.`,
            );
          }
          // Wie bei `sammelStatusAendern`: nur die Basis = voller Erfolg = Toast, sonst Teilerfolg = Stapel; ohne Konflikte
          // (Task 10, R4) mit der Ursache der ersten gescheiterten Bestellung hinter dem Satz.
          if (teile.length === 1) zeigeMeldung(basis);
          else if (conflictIds.size === 0) meldeNachladeFehler(nachladeFehler[0]!.ursache, teile.join(" "));
          else meldeFehler(teile.join(" "));
        });
      })
      .catch((fehler: unknown) => {
        console.error("Sammel-Bereichswechsel konnte nicht geschrieben werden:", fehler);
        meldeFehler(fehler, "Die Bestellungen konnten nicht verschoben werden.");
      });
  }

  return {
    statusAendern: sammelStatusAendern,
    gruppeAendern: boardGruppeSammelAendern,
    laeuft: sammelStatusLaeuft,
  };
}
